import React, { useState, useRef, useEffect, useMemo } from 'react';
import { format, parseISO, startOfDay, addHours, differenceInMinutes, addMinutes } from 'date-fns';
import { ClockIcon, XMarkIcon, CheckCircleIcon, PlayIcon } from '@heroicons/react/24/outline';
import { Event, Todo } from '../types';
import { useEvents } from '../hooks/useEvents';
import { useNotification } from '../hooks/useNotification';

interface DayScheduleProps {
  date: Date;
  onEventClick?: (event: Event) => void;
  onTimeBlockClick?: (startTime: Date) => void;
  onTaskClick?: (task: Todo) => void;
  tasks?: Todo[];
  onTaskDrop?: (taskId: string, startTime: Date) => void;
}

export const DaySchedule: React.FC<DayScheduleProps> = ({ date, onEventClick, onTimeBlockClick, onTaskClick, tasks = [], onTaskDrop }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const scheduleRef = useRef<HTMLDivElement>(null);
  const { events, deleteEvent, updateEvent } = useEvents(format(date, 'yyyy-MM-dd'));
  const { showNotification } = useNotification();
  const [draggedOverHour, setDraggedOverHour] = useState<string | null>(null);
  
  // Drag state
  const [dragging, setDragging] = useState<{
    eventId: string;
    type: 'move' | 'resize-top' | 'resize-bottom';
    startY: number;
    startTime: Date;
    startDuration: number;
  } | null>(null);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (scheduleRef.current && format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')) {
      const currentHour = new Date().getHours();
      const hourHeight = 80; // Height of each hour block
      const scrollPosition = currentHour * hourHeight - 200; // Center current time
      scheduleRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [date]);

  // Generate 24 hours for the day
  const hours = useMemo(() => {
    const dayStart = startOfDay(date);
    return Array.from({ length: 24 }, (_, i) => addHours(dayStart, i));
  }, [date]);

  // Calculate overlapping events for proper layout
  const getOverlappingEvents = (events: Event[]) => {
    const overlaps: { [key: string]: { column: number; totalColumns: number } } = {};
    
    // Group events by time overlap
    const overlapGroups: Event[][] = [];
    
    events.forEach((event) => {
      const eventStart = parseISO(event.start_time);
      const eventEnd = parseISO(event.end_time);
      
      // Find which group this event belongs to
      let foundGroup = false;
      
      for (const group of overlapGroups) {
        const hasOverlap = group.some(groupEvent => {
          const groupStart = parseISO(groupEvent.start_time);
          const groupEnd = parseISO(groupEvent.end_time);
          return eventStart < groupEnd && eventEnd > groupStart;
        });
        
        if (hasOverlap) {
          group.push(event);
          foundGroup = true;
          break;
        }
      }
      
      if (!foundGroup) {
        overlapGroups.push([event]);
      }
    });
    
    // Assign columns within each group
    overlapGroups.forEach(group => {
      const totalColumns = group.length;
      
      group.forEach((event, index) => {
        overlaps[event.id] = {
          column: index,
          totalColumns
        };
      });
    });
    
    return overlaps;
  };
  
  const overlappingEvents = getOverlappingEvents(events);

  // Filter tasks for the selected date
  const dayTasks = useMemo(() => {
    const selectedDateStr = format(date, 'yyyy-MM-dd');

    return tasks.filter(task => {
      // Show tasks with horizon 'now' (current focus)
      if (task.horizon === 'now') {
        // If it has a deadline on this date, show it
        if (task.deadline) {
          const deadlineStr = task.deadline.split('T')[0];
          if (deadlineStr === selectedDateStr) return true;
        }
      }

      // Show tasks with a deadline on this date
      if (task.deadline) {
        const deadlineStr = task.deadline.split('T')[0];
        if (deadlineStr === selectedDateStr) return true;
      }

      // Also show tasks completed on this date
      if (task.status === 'completed' && task.completed_date) {
        const completedDateStr = task.completed_date.split('T')[0];
        if (completedDateStr === selectedDateStr) return true;
      }

      return false;
    });
  }, [tasks, date]);

  // Calculate task positioning - place them in a sidebar
  const getTaskStyle = (task: Todo, index: number) => {
    // Use deadline or completed_date for time positioning
    let timeToUse = task.deadline;

    // Use completion time for completed tasks if available
    if (task.status === 'completed' && task.completed_date) {
      timeToUse = task.completed_date;
    }

    if (!timeToUse) return null;
    
    const taskTime = parseISO(timeToUse);
    const dayStart = startOfDay(date);
    const startMinutes = differenceInMinutes(taskTime, dayStart);
    
    // Position based on time, with offset for multiple tasks at same time
    const top = (startMinutes / 60) * 80; // 80px per hour
    const height = 60; // Fixed height for tasks
    
    return {
      top: `${top + (index % 3) * 20}px`, // Slight offset for multiple tasks
      height: `${height}px`,
      right: '8px',
      width: '200px',
      zIndex: 10,
    };
  };

  // Calculate position and height for events
  const getEventStyle = (event: Event) => {
    const eventStart = parseISO(event.start_time);
    const eventEnd = parseISO(event.end_time);
    const dayStart = startOfDay(date);
    
    const startMinutes = differenceInMinutes(eventStart, dayStart);
    const durationMinutes = differenceInMinutes(eventEnd, eventStart);
    
    const top = (startMinutes / 60) * 80; // 80px per hour
    const height = Math.max((durationMinutes / 60) * 80, 20); // Minimum height of 20px
    
    // Handle overlapping events
    const overlap = overlappingEvents[event.id];
    
    // For overlapping events, we need to account for the 4px margin on each side
    // to ensure the total width matches single events
    let width, left;
    if (overlap && overlap.totalColumns > 1) {
      // Calculate width as percentage of the available area (minus 8px total margin)
      const availableWidthPercent = 100;
      const columnWidthPercent = availableWidthPercent / overlap.totalColumns;
      const columnPositionPercent = (overlap.column / overlap.totalColumns) * availableWidthPercent;
      
      width = `calc(${columnWidthPercent}% - 8px)`;
      left = `calc(${columnPositionPercent}% + 4px)`;
    } else {
      width = 'calc(100% - 8px)';
      left = '4px';
    }
    
    return {
      top: `${top}px`,
      height: `${height}px`,
      width,
      left,
      backgroundColor: event.color || '#3B82F6',
    };
  };

  // Calculate current time indicator position
  const getCurrentTimePosition = () => {
    if (format(date, 'yyyy-MM-dd') !== format(currentTime, 'yyyy-MM-dd')) {
      return null;
    }
    
    const dayStart = startOfDay(date);
    const minutesSinceStart = differenceInMinutes(currentTime, dayStart);
    const position = (minutesSinceStart / 60) * 80; // 80px per hour
    
    return position;
  };
  
  // Handle drag start
  const handleDragStart = (e: React.MouseEvent, event: Event, type: 'move' | 'resize-top' | 'resize-bottom') => {
    e.stopPropagation();
    e.preventDefault();
    
    const rect = scheduleRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const startTime = parseISO(event.start_time);
    const endTime = parseISO(event.end_time);
    const duration = differenceInMinutes(endTime, startTime);
    
    setDragging({
      eventId: event.id,
      type,
      startY: e.clientY - rect.top + scheduleRef.current.scrollTop,
      startTime,
      startDuration: duration
    });
  };
  
  // Handle mouse move during drag
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging || !scheduleRef.current) return;
      
      const rect = scheduleRef.current.getBoundingClientRect();
      const currentY = e.clientY - rect.top + scheduleRef.current.scrollTop;
      const deltaY = currentY - dragging.startY;
      
      const event = events.find(ev => ev.id === dragging.eventId);
      if (!event) return;
      
      let newStartTime = dragging.startTime;
      let newDuration = dragging.startDuration;
      
      if (dragging.type === 'move') {
        // Move the entire event
        const deltaMinutes = Math.round((deltaY / 80) * 60 / 5) * 5; // Round to 5-minute increments
        newStartTime = addMinutes(dragging.startTime, deltaMinutes);
      } else if (dragging.type === 'resize-top') {
        // Resize from the top (change start time, keep end time)
        const deltaMinutes = Math.round((deltaY / 80) * 60 / 5) * 5;
        newStartTime = addMinutes(dragging.startTime, deltaMinutes);
        const originalEndTime = addMinutes(dragging.startTime, dragging.startDuration);
        newDuration = differenceInMinutes(originalEndTime, newStartTime);
        newDuration = Math.max(5, newDuration); // Minimum 5 minutes
      } else if (dragging.type === 'resize-bottom') {
        // Resize from the bottom (change end time, keep start time)
        const deltaMinutes = Math.round((deltaY / 80) * 60 / 5) * 5;
        newDuration = Math.max(5, dragging.startDuration + deltaMinutes);
      }
      
      // Prevent dragging outside day boundaries
      const dayStart = startOfDay(date);
      const dayEnd = addHours(dayStart, 24);
      const newEndTime = addMinutes(newStartTime, newDuration);
      
      if (newStartTime < dayStart) {
        newStartTime = dayStart;
        if (dragging.type === 'resize-top') {
          const originalEndTime = addMinutes(dragging.startTime, dragging.startDuration);
          newDuration = differenceInMinutes(originalEndTime, newStartTime);
        }
      }
      
      if (newEndTime > dayEnd) {
        if (dragging.type === 'resize-bottom') {
          newDuration = differenceInMinutes(dayEnd, newStartTime);
        } else {
          newStartTime = addMinutes(dayEnd, -newDuration);
        }
      }
    };
    
    const handleMouseUp = async () => {
      if (!dragging) return;
      
      const event = events.find(ev => ev.id === dragging.eventId);
      if (!event) {
        setDragging(null);
        return;
      }
      
      const rect = scheduleRef.current?.getBoundingClientRect();
      if (!rect) {
        setDragging(null);
        return;
      }
      
      // Calculate final position using the last mouse event
      const lastMouseEvent = window.event as MouseEvent;
      const currentY = lastMouseEvent.clientY - rect.top + scheduleRef.current.scrollTop;
      const deltaY = currentY - dragging.startY;
      
      let newStartTime = dragging.startTime;
      let newDuration = dragging.startDuration;
      
      if (dragging.type === 'move') {
        const deltaMinutes = Math.round((deltaY / 80) * 60 / 5) * 5;
        newStartTime = addMinutes(dragging.startTime, deltaMinutes);
      } else if (dragging.type === 'resize-top') {
        const deltaMinutes = Math.round((deltaY / 80) * 60 / 5) * 5;
        newStartTime = addMinutes(dragging.startTime, deltaMinutes);
        const originalEndTime = addMinutes(dragging.startTime, dragging.startDuration);
        newDuration = Math.max(5, differenceInMinutes(originalEndTime, newStartTime));
      } else if (dragging.type === 'resize-bottom') {
        const deltaMinutes = Math.round((deltaY / 80) * 60 / 5) * 5;
        newDuration = Math.max(5, dragging.startDuration + deltaMinutes);
      }
      
      // Prevent dragging outside day boundaries
      const dayStart = startOfDay(date);
      const dayEnd = addHours(dayStart, 24);
      let newEndTime = addMinutes(newStartTime, newDuration);
      
      if (newStartTime < dayStart) {
        newStartTime = dayStart;
        if (dragging.type === 'resize-top') {
          const originalEndTime = addMinutes(dragging.startTime, dragging.startDuration);
          newDuration = differenceInMinutes(originalEndTime, newStartTime);
          newEndTime = originalEndTime;
        } else {
          newEndTime = addMinutes(newStartTime, newDuration);
        }
      }
      
      if (newEndTime > dayEnd) {
        if (dragging.type === 'resize-bottom') {
          newDuration = differenceInMinutes(dayEnd, newStartTime);
          newEndTime = dayEnd;
        } else {
          newStartTime = addMinutes(dayEnd, -newDuration);
          newEndTime = dayEnd;
        }
      }
      
      // Format times for API using the same format as EventModal
      const formatForAPI = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };
      
      // Update the event
      try {
        await updateEvent({
          eventId: event.id,
          event: {
            ...event,
            start_time: formatForAPI(newStartTime),
            end_time: formatForAPI(newEndTime)
          }
        });
        showNotification('Event updated successfully', 'success');
      } catch (error) {
        console.error('Failed to update event:', error);
        showNotification('Failed to update event', 'error');
      }
      
      setDragging(null);
    };
    
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, events, updateEvent, showNotification, date]);

  const handleHourClick = (hour: Date) => {
    if (onTimeBlockClick) {
      onTimeBlockClick(hour);
    }
  };

  // Handle drag over for hour blocks
  const handleHourDragOver = (e: React.DragEvent, hour: Date) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if we have valid drag data
    if (e.dataTransfer.types.includes('text/plain')) {
      e.dataTransfer.dropEffect = 'copy';
      setDraggedOverHour(hour.toISOString());
    }
  };

  const handleHourDragLeave = () => {
    setDraggedOverHour(null);
  };

  // Handle drop on hour blocks
  const handleHourDrop = (e: React.DragEvent, hour: Date) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverHour(null);
    
    // Get task ID from dataTransfer - try multiple formats
    const taskId = e.dataTransfer.getData('text/plain') || 
                   e.dataTransfer.getData('Text') ||
                   e.dataTransfer.getData('text');
    
    // Debug: Show notification if no task ID found
    if (!taskId && onTaskDrop) {
      showNotification('Unable to get task data. Please try again.', 'error');
      return;
    }
    
    if (taskId && onTaskDrop) {
      // Calculate the exact time based on mouse position within the hour block
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      const minuteOffset = Math.round((relativeY / rect.height) * 60 / 15) * 15; // Round to 15-minute intervals
      const dropTime = addMinutes(hour, minuteOffset);
      
      onTaskDrop(taskId, dropTime);
    }
  };


  const currentTimePosition = getCurrentTimePosition();
  const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
          {format(date, 'EEEE, MMMM d')}
        </h3>
      </div>

      <div ref={scheduleRef} className="flex-1 overflow-y-auto relative">
        <div className="relative">
          {/* Hour blocks */}
          {hours.map((hour) => (
            <div
              key={hour.toISOString()}
              className="flex border-b border-gray-100 hover:bg-gray-50 transition-colors"
              style={{ height: '80px' }}
            >
              <div className="w-20 flex-shrink-0 py-2 px-3 text-sm text-gray-500 border-r border-gray-100">
                {format(hour, 'h:mm a')}
              </div>
              <div
                className={`flex-1 relative cursor-pointer transition-colors ${
                  draggedOverHour === hour.toISOString() ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleHourClick(hour)}
                onDragOver={(e) => handleHourDragOver(e, hour)}
                onDragLeave={handleHourDragLeave}
                onDrop={(e) => handleHourDrop(e, hour)}
                onDragEnter={(e) => {
                  e.preventDefault();
                }}
              >
                {/* Visual indicator when dragging */}
                {draggedOverHour === hour.toISOString() && (
                  <div className="absolute inset-0 border-2 border-blue-400 border-dashed bg-blue-50 opacity-50 pointer-events-none" />
                )}
              </div>
            </div>
          ))}

          {/* Events */}
          <div className="absolute inset-0 pointer-events-none" style={{ marginLeft: '5rem' }}>
            {events.map((event) => {
              const isDraggingThis = dragging?.eventId === event.id;
              return (
                <div
                  key={event.id}
                  className={`absolute mx-1 rounded-md shadow-sm border border-gray-200 pointer-events-auto overflow-hidden transition-shadow ${
                    isDraggingThis ? 'shadow-lg ring-2 ring-blue-500' : 'hover:shadow-md'
                  }`}
                  style={getEventStyle(event)}
                >
                  {/* Resize handle - top */}
                  {!event.is_from_google && (
                    <div
                      className="absolute top-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-white hover:bg-opacity-50 transition-colors"
                      onMouseDown={(e) => handleDragStart(e, event, 'resize-top')}
                    />
                  )}
                  
                  {/* Main content area - draggable */}
                  <div
                    className={`p-2 ${event.is_from_google ? 'cursor-pointer' : 'cursor-move'} h-full`}
                    onClick={() => onEventClick?.(event)}
                    onMouseDown={(e) => {
                      // Only start drag if not clicking on resize handles and not from Google
                      if (!event.is_from_google) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        if (y > 4 && y < rect.height - 4) {
                          handleDragStart(e, event, 'move');
                        }
                      }
                    }}
                  >
                    <div className="flex items-start justify-between h-full">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate flex items-center gap-1">
                          {event.is_from_google && (
                            <span className="text-xs" title="From Google Calendar">📅</span>
                          )}
                          {event.title}
                        </p>
                        {event.description && (
                          <p className="text-xs text-white/80 truncate mt-0.5">
                            {event.description}
                          </p>
                        )}
                        <p className="text-xs text-white/70 mt-1">
                          {format(parseISO(event.start_time), 'h:mm a')} - {format(parseISO(event.end_time), 'h:mm a')}
                        </p>
                      </div>
                      {!event.is_from_google && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this event?')) {
                              deleteEvent(event.id);
                            }
                          }}
                          className="opacity-0 hover:opacity-100 transition-opacity p-0.5 hover:bg-white/20 rounded"
                        >
                          <XMarkIcon className="h-4 w-4 text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Resize handle - bottom */}
                  {!event.is_from_google && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-white hover:bg-opacity-50 transition-colors"
                      onMouseDown={(e) => handleDragStart(e, event, 'resize-bottom')}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Tasks */}
          <div className="absolute inset-0 pointer-events-none" style={{ marginLeft: '5rem' }}>
            {dayTasks.map((task, index) => {
              const taskStyle = getTaskStyle(task, index);
              if (!taskStyle) return null;
              
              return (
                <div
                  key={task.id}
                  className="absolute pointer-events-auto cursor-pointer transition-shadow hover:shadow-md"
                  style={taskStyle}
                  onClick={() => onTaskClick?.(task)}
                >
                  <div className={`h-full rounded-lg border-l-4 bg-white shadow-sm border-gray-200 p-2 ${
                    task.status === 'completed' 
                      ? 'border-l-green-500 bg-green-50' 
                      : task.status === 'in_progress'
                      ? 'border-l-yellow-500 bg-yellow-50'
                      : 'border-l-blue-500 bg-blue-50'
                  }`}>
                    <div className="flex items-start justify-between h-full">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-1 mb-1">
                          {task.status === 'completed' ? (
                            <CheckCircleIcon className="w-3 h-3 text-green-600 flex-shrink-0" />
                          ) : (
                            <PlayIcon className="w-3 h-3 text-blue-600 flex-shrink-0" />
                          )}
                          <span className={`text-xs font-medium truncate ${
                            task.status === 'completed' ? 'text-green-800 line-through' : 'text-gray-900'
                          }`}>
                            {task.title}
                          </span>
                        </div>
                        {task.description && (
                          <p className="text-xs text-gray-600 truncate">
                            {task.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          {task.horizon && (
                            <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${
                              task.horizon === 'now' ? 'bg-red-100 text-red-800' :
                              task.horizon === 'next' ? 'bg-blue-100 text-blue-800' :
                              task.horizon === 'soon' ? 'bg-yellow-100 text-yellow-800' :
                              task.horizon === 'later' ? 'bg-gray-100 text-gray-600' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {task.horizon}
                            </span>
                          )}
                          {(task.deadline || task.completed_date) && (
                            <span className="text-xs text-gray-500">
                              {format(parseISO(task.completed_date || task.deadline!), 'h:mm a')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Current time indicator */}
          {isToday && currentTimePosition !== null && (
            <div
              className="absolute left-0 right-0 pointer-events-none"
              style={{ top: `${currentTimePosition}px` }}
            >
              <div className="flex items-center">
                <div className="w-20 flex justify-end pr-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                </div>
                <div className="flex-1 h-0.5 bg-red-500"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};